// Implements: F-UIX-018
/**
 * The sidebar's usage meter (UX-03): the person's month in the active organization, or in the
 * personal scope when none is active, from `GET /usage/me/summary`. Generations only, never money
 * (OD-12). While the summary loads, fails or has no row for the scope, nothing is drawn: a meter is a
 * convenience and never a reason for an error on every page.
 */

import type { ReactElement } from "react";

import { useActiveOrganization } from "../../../shared/api/activeOrganization";
import { useSession } from "../../../shared/api/session";
import { useTranslation } from "../../../shared/i18n";
import { UsageMeter } from "../components/UsageMeter";
import { useMyUsageSummary } from "../hooks/useMyUsage";
import { BACKOFFICE_PATH } from "../model/sections";

export interface UsageMeterContainerProps {
  readonly rail: boolean;
}

export function UsageMeterContainer({ rail }: UsageMeterContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const { state } = useSession();
  const { active } = useActiveOrganization();
  const personId = state.status === "authenticated" ? state.view.user.id : "";
  const summary = useMyUsageSummary(personId, personId !== "");

  const scopeId = active?.id ?? null;
  const row = summary.data?.scopes.find((scope) => scope.organization_id === scopeId);
  if (row === undefined) return null;

  const delivered = row.generations_delivered;
  const limit = row.generations_limit;
  return (
    <UsageMeter
      delivered={delivered}
      limit={limit}
      href={`${BACKOFFICE_PATH}/usage`}
      text={limit > 0 ? t("backoffice.meter.text", { delivered, limit }) : t("backoffice.meter.unlimited", { delivered })}
      meterLabel={t("backoffice.meter.label")}
      rail={rail}
    />
  );
}
